import { useCallback, useRef, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { DocumentViewportValue } from "@/features/document-viewport/types";
import { getViewportContentCenter, MAX_VIEWPORT_ZOOM } from "@/features/document-viewport/utils";
import type { MeasurementBreakdownTargetRef } from "@/types/takeoff-pilot";
import type { MeasurementOverlayRecord } from "@/viewport-tools/measurement/overlay";

function getMeasurementGeometryBounds(geometry: MeasurementOverlayRecord["geometry"]) {
  if (geometry.type === "rect") {
    return {
      minX: geometry.x,
      minY: geometry.y,
      maxX: geometry.x + geometry.width,
      maxY: geometry.y + geometry.height,
    };
  }

  const xs = geometry.vertices.map((vertex) => vertex.x);
  const ys = geometry.vertices.map((vertex) => vertex.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

/** Zooms so normalized page bounds fill roughly half the viewport, centred on them. */
export function focusViewportOnPageBounds(
  viewport: DocumentViewportValue,
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
) {
  const center = {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
  const span = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 0.02);
  const targetZoom = Math.min(MAX_VIEWPORT_ZOOM, Math.max(3, 0.45 / span));
  const contentCenter = getViewportContentCenter(
    viewport.workspaceSize,
    viewport.viewportPadding
  );
  const pageBounds = viewport.transforms.pageRectToScreenRect({
    x: 0,
    y: 0,
    width: 1,
    height: 1,
  });
  const baseWidth = pageBounds.width / viewport.camera.zoom;
  const baseHeight = pageBounds.height / viewport.camera.zoom;

  viewport.requestZoomAt(targetZoom, contentCenter);
  viewport.setPan({
    x: baseWidth * targetZoom * (0.5 - center.x),
    y: baseHeight * targetZoom * (0.5 - center.y),
  });
}

export function useTakeoffViewerFocus({
  viewportRef, activePage, planDocumentId, pageMeasurements, onSelectPlanDocumentId,
  setSelectedTagDetectionIds, setSelectedMeasurementIds, setActiveMeasurementId, sharedPendingFocusRef,
}: {
  viewportRef: RefObject<DocumentViewportValue | null>;
  sharedPendingFocusRef?: RefObject<MeasurementBreakdownTargetRef | null>;
  activePage: number;
  planDocumentId: Id<"takeoffPilotPlanDocuments"> | null;
  pageMeasurements: MeasurementOverlayRecord[];
  onSelectPlanDocumentId: (value: string) => void;
  setSelectedTagDetectionIds: Dispatch<SetStateAction<string[]>>;
  setSelectedMeasurementIds: Dispatch<SetStateAction<Id<"takeoffPilotMeasurements">[]>>;
  setActiveMeasurementId: Dispatch<SetStateAction<Id<"takeoffPilotMeasurements"> | null>>;
}) {
  const focusedMeasurementIdByBreakdownRowRef = useRef<Record<string, string>>({});
  const localPendingFocusRef = useRef<MeasurementBreakdownTargetRef | null>(null);
  const pendingMeasurementFocusRef = sharedPendingFocusRef ?? localPendingFocusRef;
  const focusPagePoint = useCallback((center: {x:number;y:number}) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const contentCenter = getViewportContentCenter(
      viewport.workspaceSize,
      viewport.viewportPadding
    );
    const pageBounds = viewport.transforms.pageRectToScreenRect({
      x: 0,
      y: 0,
      width: 1,
      height: 1,
    });
    const baseWidth = pageBounds.width / viewport.camera.zoom;
    const baseHeight = pageBounds.height / viewport.camera.zoom;

    viewport.requestZoomAt(MAX_VIEWPORT_ZOOM, contentCenter);
    viewport.setPan({
      x: baseWidth * MAX_VIEWPORT_ZOOM * (0.5 - center.x),
      y: baseHeight * MAX_VIEWPORT_ZOOM * (0.5 - center.y),
    });
  }, []);
  const focusMeasurementGeometry = useCallback((geometry: MeasurementOverlayRecord["geometry"]) => {
    if (viewportRef.current) focusViewportOnPageBounds(viewportRef.current, getMeasurementGeometryBounds(geometry));
  }, []);
  const focusMeasurementTarget = useCallback((target: MeasurementBreakdownTargetRef) => {
    const pageRows = pageMeasurements as MeasurementOverlayRecord[];
    const isCurrentSource = String(planDocumentId ?? "") === String(target.planDocumentId);
    const isCurrentPage = activePage === target.pageNumber;

    if (isCurrentSource && isCurrentPage) {
      const measurement = pageRows.find(
        (row) => String(row._id) === String(target.measurementId)
      );
      if (measurement) {
        pendingMeasurementFocusRef.current = null;
        setSelectedTagDetectionIds([]);
        setSelectedMeasurementIds([measurement._id]);
        setActiveMeasurementId(measurement._id);
        focusMeasurementGeometry(measurement.geometry);
        return;
      }
    }

    pendingMeasurementFocusRef.current = target;
    if (!isCurrentSource) {
      onSelectPlanDocumentId(String(target.planDocumentId));
      return;
    }
    if (!isCurrentPage) {
      viewportRef.current?.goToPage(target.pageNumber);
    }
  }, [activePage, focusMeasurementGeometry, onSelectPlanDocumentId, pageMeasurements, planDocumentId]);

  const focusMeasurementBreakdownRow = useCallback((args: {
    rowKey: string;
    direction: "previous" | "next";
    targets: MeasurementBreakdownTargetRef[];
  }) => {
    if (args.targets.length === 0) {
      return;
    }

    const direction = args.direction === "previous" ? -1 : 1;
    const currentMeasurementId = focusedMeasurementIdByBreakdownRowRef.current[args.rowKey];
    const currentIndex = currentMeasurementId
      ? args.targets.findIndex(
          (target) => String(target.measurementId) === currentMeasurementId
        )
      : -1;
    const baseIndex = currentIndex === -1 ? (direction === 1 ? -1 : 0) : currentIndex;
    const nextTarget =
      args.targets[(baseIndex + direction + args.targets.length) % args.targets.length];
    if (!nextTarget) {
      return;
    }

    focusedMeasurementIdByBreakdownRowRef.current = {
      ...focusedMeasurementIdByBreakdownRowRef.current,
      [args.rowKey]: String(nextTarget.measurementId),
    };
    focusMeasurementTarget(nextTarget);
  }, [focusMeasurementTarget]);

  return { focusPagePoint, focusMeasurementGeometry, focusMeasurementBreakdownRow, pendingMeasurementFocusRef };
}
